import React, { useState } from "react";
import "./QuesBody.scss";
import Questions from "./Questions";
import QuesPublishPopup from "./QuesPublishPopup";
import { Snackbar } from "@mui/material";
import { getToken } from "../../auth/authConfig";
import QuestionSectionDeletePopup from "./QuestionSectionDeletePopup";

const QuesBody = ({
  title,
  quesData,
  setQuestionsData,
  formType,
  formStatus,
  setFormStatus,
}) => {
  const [isAddingAuditPrompt, setIsAddingAuditPrompt] = useState(null);
  const [newAuditPrompt, setNewAuditPrompt] = useState("");
  const [editAuditPrompt, setEditAuditPrompt] = useState({
    sectionOrderId: null,
    value: "",
  });
  const [editSectionId, setEditSectionId] = useState(null);
  const [editSectionValue, setEditSectionValue] = useState("");
  const [isAddingSection, setIsAddingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [isAddingSubSection, setIsAddingSubSection] = useState(null);
  const [newSubSectionName, setNewSubSectionName] = useState("");
  const [isSaving, setIsSaving] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [open, setOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSectionDeletePopup, setOpenSectionDeletePopup] = useState(false);
  const [sectionOrderIdToDelete, setSectionOrderIdToDelete] = useState(null);

  // Add Audit Prompt
  const handleAddAuditPrompt = (sectionOrderId) => {
    if (newAuditPrompt.trim()) {
      setQuestionsData(
        quesData.map((q) =>
          q.sectionOrderId === sectionOrderId
            ? {
                ...q,
                prompt: {
                  ...q.prompt,
                  header: "Auditor Prompt:",
                  list: [...(q.prompt.list || []), newAuditPrompt],
                },
              }
            : q
        )
      );
      setNewAuditPrompt("");
      setIsAddingAuditPrompt(null);
    }
  };

  // Your other handlers can go here if needed

  const handleQuestionData = (questionObj, sectionOrderId) => {
    setQuestionsData((prev) => {
      return prev.map((q) => {
        if (q.sectionOrderId === sectionOrderId) {
          // If questionObj has deleteSubSection flag, remove the specific sub-section
          if (questionObj && questionObj.deleteSubSection) {
            const updatedSubsections = q.subsection.filter(
              (subSection) => subSection.subSectionOrderId !== questionObj.subSectionOrderId
            );
            return { ...q, subsection: updatedSubsections };
          }
          
          // questionObj is a single object, update the matching subsection
          const updatedSubsections = q.subsection.map((subSection) => {
            if (
              questionObj &&
              questionObj.subSectionOrderId === subSection.subSectionOrderId
            ) {
              return { ...subSection, ...questionObj };
            }
            return subSection;
          });
          return { ...q, subsection: updatedSubsections };
        }
        return q;
      });
    });
  };

  const handleAddNewSection = () => {
    if (!newSectionName.trim()) return;

    if (!quesData || quesData.length === 0) {
      // Case 1: Empty state - create new structure
      setQuestionsData([
        {
          prompt: {
            header: "Auditor Prompt:",
            list: [],
          },
          section: newSectionName,
          sectionOrderId: 0,
          subsection: [],
        },
      ]);
    } else {
      // Case 2: Add to existing data
      const newSectionOrderId = Math.max(
        ...quesData.map((q) => q.sectionOrderId)
      );
      setQuestionsData([
        ...quesData,
        {
          prompt: {
            header: "Auditor Prompt:",
            list: [],
          },
          section: newSectionName,
          sectionOrderId: newSectionOrderId + 1,
          subsection: [],
        },
      ]);
    }

    setNewSectionName("");
    setIsAddingSection(false);
  };

  const handleAddNewSubSection = (sectionOrderId) => {
    if (!newSubSectionName.trim()) return;

    setQuestionsData((prev) =>
      prev.map((section) => {
        if (section.sectionOrderId === sectionOrderId) {
          // If subsection doesn't exist or is empty, create new array with first subsection
          if (!section.subsection || section.subsection.length === 0) {
            return {
              ...section,
              subsection: [
                {
                  subSectionName: newSubSectionName,
                  subSectionOrderId: 0,
                  questions: [],
                },
              ],
            };
          }

          // If subsection array exists, add new subsection to it
          const newSubSectionOrderId =
            section.subsection[section.subsection.length - 1]
              .subSectionOrderId + 1;
          return {
            ...section,
            subsection: [
              ...section.subsection,
              {
                subSectionName: newSubSectionName,
                subSectionOrderId: newSubSectionOrderId,
                questions: [],
              },
            ],
          };
        }
        return section;
      })
    );

    setNewSubSectionName("");
    setIsAddingSubSection(null);
  };

  const handleSave = async (statusFlag) => {
    setOpen(false);
    const token = await getToken();
    const payload = {
      protocol_type: formType,
      status: statusFlag,
      data: quesData,
    };
    try {
      setIsSaving(statusFlag);
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/super/createProtocol`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "oidc-token": token,
          },
          body: JSON.stringify(payload), // Empty payload as requested
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save protocol");
      }

      const data = await response.json();
      setIsSaving("");
      setOpen(true);

      if (statusFlag === "Draft") {
        setSnackbarMessage("Protocol Saved successfully!");
      } else if (statusFlag === "Publish") {
        setFormStatus("Publish");
        setSnackbarMessage("Protocol Published successfully!");
      }
      // You can add success handling here if needed
    } catch (error) {
      console.error("Error saving protocol:", error);
      setIsSaving("");
      // You can add error handling here if needed
    } finally {
      setIsSaving("");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div id="ques-body" className="ques-body-cont">
      {formStatus === "Publish" && (
        <div className="form-edit-cont">
          <button
            className="form-edit-btn"
            onClick={() => setFormStatus("Draft")}
          >
            Edit
          </button>
        </div>
      )}
      <div>
        {quesData &&
          quesData.length > 0 &&
          quesData.map((ques, index) => (
            <div key={ques.sectionOrderId} className="ques-body-cont">
              <div className="ques-title-cont">
                <div className="ques-title">
                  {editSectionId === ques.sectionOrderId ? (
                    <div className="ques-add-input-container">
                      <input
                        type="text"
                        value={editSectionValue}
                        onChange={(e) => setEditSectionValue(e.target.value)}
                        className="ques-add-input"
                        autoFocus
                      />
                      <div className="ques-add-buttons">
                        <button
                          className="ques-add-btn"
                          onClick={() => {
                            setQuestionsData(
                              quesData.map((q) =>
                                q.sectionOrderId === ques.sectionOrderId
                                  ? { ...q, section: editSectionValue }
                                  : q
                              )
                            );
                            setEditSectionId(null);
                            setEditSectionValue("");
                          }}
                          disabled={!editSectionValue.trim()}
                        >
                          Save
                        </button>
                        <button
                          className="ques-cancel-btn"
                          onClick={() => {
                            setEditSectionId(null);
                            setEditSectionValue("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3>{ques.section}</h3>
                    </>
                  )}
                </div>
                {formStatus !== "Publish" && (
                  <div className="ques-section-btns">
                    <div className="ques-edit-btn" onClick={() => {setOpenSectionDeletePopup(true)
                    setSectionOrderIdToDelete(ques.sectionOrderId)
                    }}>
                      <p>Delete</p>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V9C18 7.9 17.1 7 16 7H8C6.9 7 6 7.9 6 9V19ZM18 4H15.5L14.79 3.29C14.61 3.11 14.35 3 14.09 3H9.91C9.65 3 9.39 3.11 9.21 3.29L8.5 4H6C5.45 4 5 4.45 5 5C5 5.55 5.45 6 6 6H18C18.55 6 19 5.55 19 5C19 4.45 18.55 4 18 4Z"
                          fill="#8D8D8D"
                        />
                      </svg>
                    </div>
                    <div
                      className="ques-edit-btn"
                      onClick={() => {
                        setEditSectionId(ques.sectionOrderId);
                        setEditSectionValue(ques.section);
                      }}
                    >
                      <p>Edit</p>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="ques-edit-icon"
                      >
                        <path
                          d="M3 17.4585V20.4985C3 20.7785 3.22 20.9985 3.5 20.9985H6.54C6.67 20.9985 6.8 20.9485 6.89 20.8485L17.81 9.93854L14.06 6.18854L3.15 17.0985C3.05 17.1985 3 17.3185 3 17.4585ZM20.71 7.03854C20.8027 6.94602 20.8762 6.83613 20.9264 6.71516C20.9766 6.59419 21.0024 6.46451 21.0024 6.33354C21.0024 6.20257 20.9766 6.07289 20.9264 5.95191C20.8762 5.83094 20.8027 5.72105 20.71 5.62854L18.37 3.28854C18.2775 3.19583 18.1676 3.12229 18.0466 3.07211C17.9256 3.02192 17.796 2.99609 17.665 2.99609C17.534 2.99609 17.4043 3.02192 17.2834 3.07211C17.1624 3.12229 17.0525 3.19583 16.96 3.28854L15.13 5.11854L18.88 8.86854L20.71 7.03854Z"
                          fill="#8D8D8D"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Auditor Prompt Section */}
              {/* Auditor Prompt Section */}
              <div className="ques-objective-cont">
                <div className="ques-objective">
                  <div className="ques-objective-text-cont">
                    <div className="ques-obj">
                      <div className="ques-audit-header-row">
                        <p className="ques-audit-prom-txt">
                          {ques?.prompt?.header || "Auditor Prompts:"}
                        </p>
                      </div>

                      {ques?.prompt?.list && ques?.prompt?.list.length > 0 && (
                        <ol className="ques-audit-prom-list">
                          {editAuditPrompt.sectionOrderId ===
                          ques.sectionOrderId ? (
                            // Edit mode: show each point as a separate input
                            <div className="ques-add-input-container">
                              {ques.prompt.list.map((_, idx) => (
                                <input
                                  key={idx}
                                  type="text"
                                  value={editAuditPrompt.value[idx] || ""}
                                  onChange={(e) => {
                                    const updated = [...editAuditPrompt.value];
                                    updated[idx] = e.target.value;
                                    setEditAuditPrompt({
                                      sectionOrderId: ques.sectionOrderId,
                                      value: updated,
                                    });
                                  }}
                                  className="ques-add-input"
                                  autoFocus={idx === 0}
                                  style={{ marginBottom: "8px" }}
                                />
                              ))}
                              <div className="ques-add-buttons">
                                <button
                                  className="ques-add-btn"
                                  onClick={() => {
                                    setQuestionsData(
                                      quesData.map((q) =>
                                        q.sectionOrderId === ques.sectionOrderId
                                          ? {
                                              ...q,
                                              prompt: {
                                                ...q.prompt,
                                                list: editAuditPrompt.value
                                                  .map((item) => item.trim())
                                                  .filter(Boolean),
                                              },
                                            }
                                          : q
                                      )
                                    );
                                    setEditAuditPrompt({
                                      sectionOrderId: null,
                                      value: [],
                                    });
                                  }}
                                  disabled={editAuditPrompt.value.every(
                                    (v) => !v.trim()
                                  )}
                                >
                                  Save
                                </button>
                                <button
                                  className="ques-cancel-btn"
                                  onClick={() =>
                                    setEditAuditPrompt({
                                      sectionOrderId: null,
                                      value: [],
                                    })
                                  }
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            ques.prompt.list &&
                            ques.prompt.list.map((prompt, idx) => (
                              <li
                                className="ques-audit-prom-list-item"
                                key={idx}
                              >
                                {prompt}
                              </li>
                            ))
                          )}
                        </ol>
                      )}

                      {isAddingAuditPrompt === ques.sectionOrderId && (
                        <div className="ques-add-input-container">
                          <input
                            type="text"
                            value={newAuditPrompt}
                            onChange={(e) => setNewAuditPrompt(e.target.value)}
                            placeholder="Enter audit prompt"
                            className="ques-add-input"
                            autoFocus
                          />
                          <div className="ques-add-buttons">
                            <button
                              className="ques-add-btn"
                              onClick={() =>
                                handleAddAuditPrompt(ques.sectionOrderId)
                              }
                              disabled={!newAuditPrompt.trim()}
                            >
                              Add
                            </button>
                            <button
                              className="ques-cancel-btn"
                              onClick={() => {
                                setNewAuditPrompt("");
                                setIsAddingAuditPrompt(null);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Show "Add Audit Prompt" button when not editing and not in adding mode */}
                      {editAuditPrompt.sectionOrderId !== ques.sectionOrderId &&
                        isAddingAuditPrompt !== ques.sectionOrderId &&
                        formStatus !== "Publish" && (
                          <div
                            className="ques-add-new"
                            onClick={() =>
                              setIsAddingAuditPrompt(ques.sectionOrderId)
                            }
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="30"
                              height="30"
                              viewBox="0 0 30 30"
                              fill="none"
                            >
                              <path
                                d="M15 8.75C14.3125 8.75 13.75 9.3125 13.75 10V13.75H10C9.3125 13.75 8.75 14.3125 8.75 15C8.75 15.6875 9.3125 16.25 10 16.25H13.75V20C13.75 20.6875 14.3125 21.25 15 21.25C15.6875 21.25 16.25 20.6875 16.25 20V16.25H20C20.6875 16.25 21.25 15.6875 21.25 15C21.25 14.3125 20.6875 13.75 20 13.75H16.25V10C16.25 9.3125 15.6875 8.75 15 8.75ZM15 2.5C8.1 2.5 2.5 8.1 2.5 15C2.5 21.9 8.1 27.5 15 27.5C21.9 27.5 27.5 21.9 27.5 15C27.5 8.1 21.9 2.5 15 2.5ZM15 25C9.4875 25 5 20.5125 5 15C5 9.4875 9.4875 5 15 5C20.5125 5 25 9.4875 25 15C25 20.5125 20.5125 25 15 25Z"
                                fill="#8D8D8D"
                              />
                            </svg>
                            <p className="ques-add-new-txt">Add Audit Prompt</p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {/* Add/Edit Audit Prompt button - only show when there are existing prompts */}
                {formStatus !== "Publish" &&
                  ques?.prompt?.list &&
                  ques.prompt.list.length > 0 &&
                  editAuditPrompt.sectionOrderId !== ques.sectionOrderId &&
                  isAddingAuditPrompt !== ques.sectionOrderId && (
                    <div
                      className="ques-edit-btn"
                      onClick={() =>
                        setEditAuditPrompt({
                          sectionOrderId: ques.sectionOrderId,
                          value: [...ques.prompt.list],
                        })
                      }
                    >
                      <p>Edit</p>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        className="ques-edit-icon"
                      >
                        <path
                          d="M3 17.4585V20.4985C3 20.7785 3.22 20.9985 3.5 20.9985H6.54C6.67 20.9985 6.8 20.9485 6.89 20.8485L17.81 9.93854L14.06 6.18854L3.15 17.0985C3.05 17.1985 3 17.3185 3 17.4585ZM20.71 7.03854C20.8027 6.94602 20.8762 6.83613 20.9264 6.71516C20.9766 6.59419 21.0024 6.46451 21.0024 6.33354C21.0024 6.20257 20.9766 6.07289 20.9264 5.95191C20.8762 5.83094 20.8027 5.72105 20.71 5.62854L18.37 3.28854C18.2775 3.19583 18.1676 3.12229 18.0466 3.07211C17.9256 3.02192 17.796 2.99609 17.665 2.99609C17.534 2.99609 17.4043 3.02192 17.2834 3.07211C17.1624 3.12229 17.0525 3.19583 16.96 3.28854L15.13 5.11854L18.88 8.86854L20.71 7.03854Z"
                          fill="#8D8D8D"
                        />
                      </svg>
                    </div>
                  )}
              </div>

              {/* Subsections and Questions */}
              {/* Subsections rendering */}
              {ques?.subsection?.map((subsec, subIndex) => (
                <Questions
                  questions={subsec}
                  key={subsec.subSectionOrderId || subIndex}
                  onQuestionDataChange={handleQuestionData}
                  sectionOrderId={ques.sectionOrderId}
                  setQuestionsData={setQuestionsData}
                  formStatus={formStatus}
                />
              ))}

              {/* Add New Subsection UI */}
              {formStatus !== "Publish" && (
                <>
                  {isAddingSubSection === ques.sectionOrderId ? (
                    <div className="ques-add-new-section-input ques-add-input-container">
                      <input
                        type="text"
                        value={newSubSectionName}
                        onChange={(e) => setNewSubSectionName(e.target.value)}
                        placeholder="Enter subsection name"
                        className="ques-add-input"
                        autoFocus
                      />
                      <div className="ques-add-buttons">
                        <button
                          className="ques-add-btn"
                          onClick={() =>
                            handleAddNewSubSection(ques.sectionOrderId)
                          }
                          disabled={!newSubSectionName.trim()}
                        >
                          Add
                        </button>
                        <button
                          className="ques-cancel-btn"
                          onClick={() => {
                            setNewSubSectionName("");
                            setIsAddingSubSection(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="ques-add-new-section ques-add-new-sub-section"
                      onClick={() => setIsAddingSubSection(ques.sectionOrderId)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="30"
                        height="30"
                        viewBox="0 0 30 30"
                        fill="none"
                      >
                        <path
                          d="M15 8.75C14.3125 8.75 13.75 9.3125 13.75 10V13.75H10C9.3125 13.75 8.75 14.3125 8.75 15C8.75 15.6875 9.3125 16.25 10 16.25H13.75V20C13.75 20.6875 14.3125 21.25 15 21.25C15.6875 21.25 16.25 20.6875 16.25 20V16.25H20C20.6875 16.25 21.25 15.6875 21.25 15C21.25 14.3125 20.6875 13.75 20 13.75H16.25V10C16.25 9.3125 15.6875 8.75 15 8.75ZM15 2.5C8.1 2.5 2.5 8.1 2.5 15C2.5 21.9 8.1 27.5 15 27.5C21.9 27.5 27.5 21.9 27.5 15C27.5 8.1 21.9 2.5 15 2.5ZM15 25C9.4875 25 5 20.5125 5 15C5 9.4875 9.4875 5 15 5C20.5125 5 25 9.4875 25 15C25 20.5125 20.5125 25 15 25Z"
                          fill="#8D8D8D"
                        />
                      </svg>
                      <p className="ques-add-new-section-txt ques-add-new-sub-section-txt">
                        Add New Sub Section
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
      </div>

      {/* Add New Section */}

      {formStatus !== "Publish" && (
        <>
          {isAddingSection ? (
            <div className="ques-add-new-section-input ques-add-input-container">
              <input
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="Enter section name"
                className="ques-add-input"
                autoFocus
              />
              <div className="ques-add-buttons">
                <button
                  className="ques-add-btn"
                  onClick={handleAddNewSection}
                  disabled={!newSectionName.trim()}
                >
                  Add
                </button>
                <button
                  className="ques-cancel-btn"
                  onClick={() => {
                    setNewSectionName("");
                    setIsAddingSection(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div
              className="ques-add-new-section"
              onClick={() => setIsAddingSection(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
              >
                <path
                  d="M15 8.75C14.3125 8.75 13.75 9.3125 13.75 10V13.75H10C9.3125 13.75 8.75 14.3125 8.75 15C8.75 15.6875 9.3125 16.25 10 16.25H13.75V20C13.75 20.6875 14.3125 21.25 15 21.25C15.6875 21.25 16.25 20.6875 16.25 20V16.25H20C20.6875 16.25 21.25 15.6875 21.25 15C21.25 14.3125 20.6875 13.75 20 13.75H16.25V10C16.25 9.3125 15.6875 8.75 15 8.75ZM15 2.5C8.1 2.5 2.5 8.1 2.5 15C2.5 21.9 8.1 27.5 15 27.5C21.9 27.5 27.5 21.9 27.5 15C27.5 8.1 21.9 2.5 15 2.5ZM15 25C9.4875 25 5 20.5125 5 15C5 9.4875 9.4875 5 15 5C20.5125 5 25 9.4875 25 15C25 20.5125 20.5125 25 15 25Z"
                  fill="#ffffff"
                />
              </svg>
              <p className="ques-add-new-section-txt">Add New Section</p>
            </div>
          )}
        </>
      )}

      {formStatus !== "Publish" && (
        <div className="ques-save-btn-cont">
          <button
            className="ques-save-btn"
            onClick={() => handleSave("Draft")}
            disabled={isSaving === "Draft" || isSaving === "Publish"}
          >
            {isSaving === "Draft" ? "Saving..." : "Save"}
          </button>
          <button
            className="ques-publish-btn"
            onClick={() => setOpenPopup(true)}
            disabled={isSaving === "Draft" || isSaving === "Publish"}
          >
            {isSaving === "Publish" ? "Publishing..." : "Publish"}
          </button>
        </div>
      )}
      <QuesPublishPopup
        handleSave={handleSave}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        isSaving={isSaving}
      />

      <QuestionSectionDeletePopup
        openPopup={openSectionDeletePopup}
        setOpenPopup={setOpenSectionDeletePopup}
        setQuestionsData={setQuestionsData}
        sectionOrderId={sectionOrderIdToDelete}
      />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        onClose={handleClose}
        message={snackbarMessage}
        autoHideDuration={2000}
        className="ques-success-snackbar"
      />
    </div>
  );
};

export default QuesBody;
